<template>
  <div id="app">
    <div class="app-shell" :class="{ 'app-shell--blocked': !userStore.sessionReady }">
    <header class="modern-header">
      <div class="container">
        <div class="d-flex justify-content-between align-items-center">
          <div class="header-logo">
            <router-link to="/" class="logo-link">
              <i class="fas fa-calculator me-2"></i>
              <span class="logo-text">TCP/NTCP</span>
              <span class="logo-subtitle">Calculator</span>
            </router-link>
          </div>
          <div class="header-auth">
            <div v-if="!userStore.sessionReady" class="auth-session-check text-white-50 small">
              Проверка сессии…
            </div>
            <div v-else-if="userStore.user" class="auth-buttons">
              <router-link v-if="userStore.isApproved" to="/app/calculate" class="btn btn-start">
                <i class="fas fa-play-circle me-2"></i>
                Начать расчет
              </router-link>
<div ref="dropdownWrapper" class="dropdown-wrapper">
                <button
                  type="button"
                  class="btn btn-icon"
                  title="Профиль"
                  aria-haspopup="true"
                  :aria-expanded="dropdownOpen"
                  @click.stop="toggleDropdown"
                >
                  <i class="fas fa-user-circle"></i>
                </button>
                <div v-if="dropdownOpen" class="user-dropdown-menu">
                  <router-link to="/profile" class="user-dropdown-item" @click="dropdownOpen = false">
                    <i class="fas fa-user me-2"></i>
                    Личный кабинет
                  </router-link>
                  <router-link
                    v-if="userStore.isAdmin"
                    to="/app/admin"
                    class="user-dropdown-item"
                    @click="dropdownOpen = false"
                  >
                    <i class="fas fa-user-shield me-2"></i>
                    Администрирование
                  </router-link>
                  <div class="user-dropdown-divider"></div>
                  <button type="button" class="user-dropdown-item user-dropdown-item-logout" @click="logout">
                    <i class="fas fa-sign-out-alt me-2"></i>
                    Выход
                  </button>
                </div>
              </div>
            </div>
            <div v-else-if="userStore.sessionReady" class="login-section">
              <form @submit.prevent="loginUser" class="login-form">
                <div class="form-group-inline">
                  <label for="id_login" class="visually-hidden">Имя пользователя</label>
                  <input 
                    type="text" 
                    v-model="username" 
                    class="form-control form-control-sm" 
                    maxlength="150" 
                    required 
                    id="id_login"
                    placeholder="Имя пользователя"
                    autocomplete="username"
                  >
                </div>
                <div class="form-group-inline">
                  <label for="id_password" class="visually-hidden">Пароль</label>
                  <input 
                    type="password" 
                    v-model="password" 
                    class="form-control form-control-sm" 
                    required 
                    id="id_password"
                    placeholder="Пароль"
                    autocomplete="current-password"
                  >
                </div>
                <button type="submit" class="btn btn-login">
                  <i class="fas fa-sign-in-alt me-2"></i>
                  Войти
                </button>
              </form>
              <router-link to="/signup" class="btn btn-signup">
                <i class="fas fa-user-plus me-2"></i>
                Регистрация
              </router-link>
              <PopUp v-if="showPopup" :message="errorMessage" :isVisible="showPopup" @close="handleClosePopup" />
            </div>
          </div>
        </div>
      </div>
    </header>
    <div v-if="userStore.sessionNotice" class="auth-session-banner" role="status">
      {{ userStore.sessionNotice }}
    </div>
    <main class="main-content">
      <router-view />
    </main>
    <footer class="app-footer">
      <div class="container app-footer__content">
        <div class="app-footer__brand">
          <img src="/images/tpu_logo.svg" alt="Логотип ТПУ" class="app-footer__logo">
          <div class="app-footer__text">
            <p class="app-footer__line">Центр по применению ионизирующего излучения в медицине</p>
            <p class="app-footer__line">Национальный исследовательский Томский политехнический университет</p>
          </div>
        </div>
        <div class="app-footer__contacts">
          <p class="app-footer__contacts-label">Контакты</p>
          <p class="app-footer__contacts-name">
            <i class="fas fa-user me-2" aria-hidden="true"></i>
            Сухих Евгения
          </p>
          <a
            href="mailto:evgeniiassukhikh@tpu.ru"
            class="app-footer__contacts-email"
          >
            <i class="fas fa-envelope me-2" aria-hidden="true"></i>
            evgeniiassukhikh@tpu.ru
          </a>
        </div>
      </div>
    </footer>
    </div>
    <div
      v-if="!userStore.sessionReady"
      class="session-overlay"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="session-overlay__card">
        <div class="session-overlay__spinner" aria-hidden="true"></div>
        <div class="session-overlay__text">Проверка сессии…</div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from './stores/userStore';
import PopUp from './components/PopUp.vue';
import { apiErrorMessage } from './api/errors';
import { AUTH_FAILED, AUTH_INVALID_CREDENTIALS } from './constants/userMessages';
import { login, logout as logoutApi } from './api/modules/userApi';


export default {
  components: {
    PopUp
  },
  data() {
    return {
      user: null,
      username: '',
      password: '',
      showPopup: false,
      errorMessage: '',
      dropdownOpen: false
    };
  },
  watch: {
    dropdownOpen(val) {
      if (val) {
        this.$nextTick(() => document.addEventListener('click', this.handleClickOutside));
      } else {
        document.removeEventListener('click', this.handleClickOutside);
      }
    },
    'userStore.user'(user) {
      if (user || !this.userStore.sessionNotice) return;
      const needsAuth = this.$route.matched.some((route) => route.meta?.requiresAuth);
      if (needsAuth && this.$route.path !== '/') {
        this.$router.push('/');
      }
    },
  },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    async loginUser() {
      try {
        this.errorMessage = '';
        const response = await login({
          username: this.username,
          password: this.password,
        });
        const { access, refresh, user } = response;
        this.userStore.setTokens(access, refresh);
        this.userStore.setUser(user);
        this.user = user;
        this.username = '';
        this.password = '';
        if (Number(user?.role) === 0) {
          this.$router.push('/pending');
        }
      } catch (error) {
        const fallback = error?.response?.status === 400 ? AUTH_INVALID_CREDENTIALS : AUTH_FAILED;
        this.errorMessage = apiErrorMessage(error, fallback);
        this.showPopup = true;
      }
    },
    handleClickOutside(e) {
      if (this.$refs.dropdownWrapper && !this.$refs.dropdownWrapper.contains(e.target)) {
        this.dropdownOpen = false;
      }
    },
    async logout() {
      this.dropdownOpen = false;
      try {
        const refresh = localStorage.getItem('refreshToken');
        await logoutApi(refresh);
      } catch (_) {
        // ignore
      }
      this.userStore.clearUser();
      this.user = null;
      this.$router.push('/');
    },
    handleClosePopup() {
      this.showPopup = false;
    }
  },
};
</script>

<style scoped>
.modern-header {
  background: var(--tcp-gradient-chrome);
  color: white;
  padding: 0.35rem 0;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
  border-bottom: 1px solid rgba(126, 231, 255, 0.16);
  position: sticky;
  top: 0;
  z-index: 1000;
  overflow: visible;
}

.modern-header :deep(.container),
.modern-header :deep(.d-flex) {
  overflow: visible;
}

.header-logo .logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
  transition: transform 0.2s ease;
}

.header-logo .logo-link:hover {
  transform: translateY(-2px);
  color: white;
}

.logo-text {
  font-weight: 700;
  margin-right: 0.5rem;
}

.logo-subtitle {
  font-weight: 300;
  opacity: 0.9;
}

.header-auth {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  overflow: visible;
}

.auth-buttons {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  overflow: visible;
}

.dropdown-wrapper {
  position: relative;
  z-index: 1001;
  overflow: visible;
}

.user-dropdown-menu {
  display: block;
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 0.5rem;
  min-width: 200px;
  background: var(--tcp-color-surface-muted);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 0;
  z-index: 1001;
  overflow: hidden;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.user-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.6rem 1rem;
  border: none;
  background: transparent;
  color: var(--tcp-color-text-secondary);
  font-size: 0.95rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease;
  text-align: left;
}

.user-dropdown-item:hover {
  background: rgba(102, 126, 234, 0.1);
  color: var(--tcp-color-brand-start);
}

.user-dropdown-item-logout {
  color: var(--tcp-color-danger);
}

.user-dropdown-item-logout:hover {
  color: var(--tcp-color-danger-hover);
  background: rgba(220, 38, 38, 0.1);
}

.user-dropdown-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 0.25rem 0;
}

.login-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.login-form {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  background: rgba(8, 16, 36, 0.35);
  border: 1px solid rgba(126, 231, 255, 0.2);
  padding: 0.28rem 0.7rem;
  border-radius: 50px;
  backdrop-filter: blur(10px);
}

.form-group-inline {
  display: flex;
  flex-direction: column;
}

.form-group-inline input {
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 16px;
  padding: 0.28rem 0.75rem;
  min-width: 128px;
  min-height: 0;
  height: 1.7rem;
  font-size: 0.8rem;
  line-height: 1.2;
}

.form-group-inline input:focus {
  background: white;
  box-shadow: 0 0 0 3px rgba(126, 231, 255, 0.28);
  outline: none;
}

.btn-icon {
  background: rgba(126, 231, 255, 0.12);
  border: 1px solid rgba(126, 231, 255, 0.28);
  color: white;
  padding: 0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-icon:hover {
  background: rgba(126, 231, 255, 0.22);
  color: white;
  transform: scale(1.1);
}

.btn-start {
  background: rgba(247, 251, 255, 0.96);
  color: #1c2748;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  padding: 0.38rem 1.15rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
}

.btn-start:hover {
  background: #f7fbff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  color: #15203c;
}

.btn-login {
  background: rgba(247, 251, 255, 0.96);
  color: #1c2748;
  border: none;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.2;
  padding: 0.28rem 0.95rem;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.btn-login:hover {
  background: #f7fbff;
  transform: translateY(-2px);
  color: #15203c;
}

.btn-signup {
  background: transparent;
  border: 1px solid rgba(126, 231, 255, 0.45);
  color: #f7fbff;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.2;
  padding: 0.28rem 0.95rem;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.btn-signup:hover {
  background: rgba(126, 231, 255, 0.14);
  color: #f7fbff;
  transform: translateY(-2px);
}

.btn-logout {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(255, 255, 255, 0.8);
  color: var(--tcp-color-danger);
  font-weight: 600;
  padding: 0.6rem 1.25rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
}

.btn-logout:hover {
  background: white;
  border-color: white;
  color: var(--tcp-color-danger-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.main-content {
  min-height: calc(100vh - 80px);
}

.auth-session-banner {
  background: var(--tcp-color-warning-bg);
  color: var(--tcp-color-warning-text);
  border-bottom: 1px solid var(--tcp-color-warning-border);
  text-align: center;
  padding: 0.55rem 1rem;
  font-size: 0.92rem;
}

.app-shell {
  transition: filter 0.2s ease, opacity 0.2s ease;
}

.app-shell--blocked {
  filter: blur(4px);
  opacity: 0.85;
  pointer-events: none;
  user-select: none;
}

.session-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 12, 22, 0.22);
  backdrop-filter: blur(2px);
}

.session-overlay__card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--tcp-color-text-body);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  font-weight: 600;
}

.session-overlay__spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(102, 126, 234, 0.25);
  border-top-color: var(--tcp-color-brand-start);
  border-radius: 50%;
  animation: session-spin 0.9s linear infinite;
}

@keyframes session-spin {
  to {
    transform: rotate(360deg);
  }
}

.app-footer {
  background: var(--tcp-gradient-chrome);
  border-top: 1px solid rgba(126, 231, 255, 0.16);
  padding: 0.4rem 0;
  color: white;
}

.app-footer__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.app-footer__brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.app-footer__logo {
  width: auto;
  height: 42px;
  max-width: 196px;
  object-fit: contain;
}

.app-footer__text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.app-footer__line {
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.78rem;
  line-height: 1.25;
}

.app-footer__contacts {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.12rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.78rem;
  line-height: 1.3;
}

.app-footer__contacts-label {
  margin: 0;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
}

.app-footer__contacts-name {
  margin: 0;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.app-footer__contacts-email {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.app-footer__contacts-email:hover {
  color: white;
  opacity: 0.85;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .login-section {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .login-form {
    flex-direction: column;
    width: 100%;
  }
  
  .form-group-inline {
    width: 100%;
  }
  
  .form-group-inline input {
    min-width: 100%;
  }

  .app-footer__brand {
    align-items: flex-start;
  }

  .app-footer__logo {
    height: 32px;
    max-width: 149px;
  }

  .app-footer__contacts {
    align-items: flex-start;
    width: 100%;
  }
}
</style>
